import { useEffect, useState, useRef } from 'react';
import { subscribeToGlobalMarketPrices, registerActiveSymbols, fetchSymbolDataOnDemand } from '../firebase';
import { ALIAS_DICTIONARY, resolveSymbolAlias } from '../utils/symbolAlias';


export const useCurrentPrices = (transactions, watchlists = []) => {
  const [currentPrices, setCurrentPrices] = useState({});
  const [previousCloses, setPreviousCloses] = useState({});
  const [todayCloses, setTodayCloses] = useState({});
  const [extendedPrices, setExtendedPrices] = useState({});
  const [extendedChanges, setExtendedChanges] = useState({});
  const [highs, setHighs] = useState({});
  const [lows, setLows] = useState({});
  const fetchedSymbolsRef = useRef(new Set()); // Track symbols already sent to on-demand

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    // 1. Setup Realtime Listener (onSnapshot) from Central Market Data
    unsubscribe = subscribeToGlobalMarketPrices((pricesData) => {
       if (isMounted && pricesData) {
         const mappedPrices = { ...pricesData };
         const mappedPrev = { ...(pricesData._previousClose || {}) };
         const mappedToday = { ...(pricesData._todayClose || {}) };
         const mappedExtP = { ...(pricesData._extendedPrices || {}) };
         const mappedExtC = { ...(pricesData._extendedPercentChanges || {}) };
         const mappedHighs = { ...(pricesData._high || {}) };
         const mappedLows = { ...(pricesData._low || {}) };

         // Inject aliases so the UI can look up by alias natively
         // We force overwrite any existing alias key because the backend might have fetched
         // a wrong ticker that happens to share the alias name (e.g., WTI = W&T Offshore)
         for (const [alias, canonical] of Object.entries(ALIAS_DICTIONARY)) {
           if (pricesData[canonical] !== undefined) {
             mappedPrices[alias] = pricesData[canonical];
             if (pricesData._previousClose?.[canonical] !== undefined) mappedPrev[alias] = pricesData._previousClose[canonical];
             else delete mappedPrev[alias];
             if (pricesData._todayClose?.[canonical] !== undefined) mappedToday[alias] = pricesData._todayClose[canonical];
             else delete mappedToday[alias];
             if (pricesData._extendedPrices?.[canonical] !== undefined) mappedExtP[alias] = pricesData._extendedPrices[canonical];
             else delete mappedExtP[alias];
             if (pricesData._extendedPercentChanges?.[canonical] !== undefined) mappedExtC[alias] = pricesData._extendedPercentChanges[canonical];
             else delete mappedExtC[alias];
             if (pricesData._high?.[canonical] !== undefined) mappedHighs[alias] = pricesData._high[canonical];
             else delete mappedHighs[alias];
             if (pricesData._low?.[canonical] !== undefined) mappedLows[alias] = pricesData._low[canonical];
             else delete mappedLows[alias];
           }
         }

         if (pricesData._previousClose) setPreviousCloses(mappedPrev);
         if (pricesData._todayClose) setTodayCloses(mappedToday);
         if (pricesData._extendedPrices) setExtendedPrices(mappedExtP);
         if (pricesData._extendedPercentChanges) setExtendedChanges(mappedExtC);
         if (pricesData._high) setHighs(mappedHighs);
         if (pricesData._low) setLows(mappedLows);
         setCurrentPrices(mappedPrices);
       }
    });

    // 2. Register Active Symbols to Global Cache for Background Polling
    const transactionSymbols = transactions ? transactions.map(t => t.symbol).filter(Boolean) : [];
    
    // Extract symbols from watchlists
    const watchlistSymbols = [];
    if (watchlists && watchlists.length > 0) {
      watchlists.forEach(list => {
        if (list.symbols && Array.isArray(list.symbols)) {
          watchlistSymbols.push(...list.symbols);
        }
      });
    }

    const allSymbols = [...transactionSymbols, ...watchlistSymbols];
    
    if (allSymbols.length > 0) {
      const uniqSymbols = [...new Set(allSymbols)];
      if (uniqSymbols.length > 0) {
        // Map to canonical before registering so the backend fetches the correct data
        const canonicalSymbols = [...new Set(uniqSymbols.map(sym => resolveSymbolAlias(sym)))];
        
        // Fire and forget
        registerActiveSymbols(canonicalSymbols).catch(err => {
            console.warn("Failed to register symbols", err);
        });

        // 3. On-demand: fetch dividend/earnings data for NEW symbols
        //    Only call for symbols we haven't already requested this session
        const newSymbols = canonicalSymbols.filter(sym => !fetchedSymbolsRef.current.has(sym));
        if (newSymbols.length > 0) {
          newSymbols.forEach(sym => fetchedSymbolsRef.current.add(sym));
          // Delay slightly so app finishes loading first, then fetch SEQUENTIALLY
          // with a small gap. Firing all at once spiked Twelve Data credits
          // (each symbol = up to 20 credits for /dividends + /earnings).
          setTimeout(async () => {
            for (const sym of newSymbols) {
              if (!isMounted) break;
              await fetchSymbolDataOnDemand(sym);
              await new Promise(r => setTimeout(r, 400)); // ~spread to avoid spikes
            }
          }, 3000);
        }
      }
    }

    return () => {
       isMounted = false;
       if (unsubscribe) unsubscribe();
    };
  }, [transactions, watchlists]);

  return { currentPrices, previousCloses, todayCloses, extendedPrices, extendedChanges, highs, lows };
};
